import { useState, useEffect } from 'react';
import memoizeOne from 'memoize-one';
import { useIssueTypesAndFields } from '@atlassian/jira-business-entity-project';
import { useProject } from '@atlassian/jira-business-entity-project-hook';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import { MAX_ISSUE_TYPES_COUNT } from '../../../common/constants';
import {
	useWorkflowsV2,
	type WorkflowV2Response,
	type FetchWorkflowsV2Param,
} from '../../../services/workflow-v2';
import { removeWorkflowAssociatedWithSubtaskOnly } from '../utils';

type State = {
	data:
		| {
				hasMultipleWorkflows: boolean;
				hasExceededMaxIssueTypes: boolean;
		  }
		| undefined;
	loading: boolean;
	error: Error | undefined;
};

type GetWorkflowsCountParam = FetchWorkflowsV2Param & {
	subtaskIssueTypeId: string | undefined;
	fetchWorkflowsV2: (arg1: FetchWorkflowsV2Param) => Promise<WorkflowV2Response>;
};

const getWorkflowsCount = async (param: GetWorkflowsCountParam) => {
	const { subtaskIssueTypeId, fetchWorkflowsV2, ...otherParam } = param;
	const result = await fetchWorkflowsV2(otherParam);

	const filteredResult = removeWorkflowAssociatedWithSubtaskOnly(result, subtaskIssueTypeId);
	return filteredResult.workflows.length;
};

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
export const memoizedGetWorkflowsCount = memoizeOne(getWorkflowsCount, (newArgs, lastArgs) => {
	if (newArgs.length !== lastArgs.length) {
		return false;
	}

	const equalityFn = // we are typing it properly, so it's not mixed type
		// custom equality to check if parameters can be considered the same
		(newParam: GetWorkflowsCountParam, prevParam: GetWorkflowsCountParam): boolean =>
			newParam.cloudId === prevParam.cloudId &&
			newParam.projectAndIssueTypes.every(
				(item, index) => item.projectId === prevParam.projectAndIssueTypes[index].projectId,
			) &&
			newParam.subtaskIssueTypeId === prevParam.subtaskIssueTypeId;

	return newArgs.every((newArg, index) => equalityFn(newArg, lastArgs[index]));
});

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
export const useDetectMultipleWorkflows = ({
	shouldFetchOnMount,
}: {
	shouldFetchOnMount: boolean;
}): State => {
	const cloudId = useCloudId();
	const projectData = useProject();
	const projectId = String(projectData.id);
	const { fetchWorkflowsV2 } = useWorkflowsV2();

	const {
		data: { issueTypes },
		loading,
	} = useIssueTypesAndFields({
		issueOperation: 'VIEW',
	});

	const hasExceededMaxIssueTypes = issueTypes.length > MAX_ISSUE_TYPES_COUNT;
	const subtaskIssueTypeId = issueTypes.find((issueType) => issueType.subtask)?.id;

	const [state, setState] = useState<State>({
		data: undefined,
		loading: shouldFetchOnMount,
		error: undefined,
	});

	useEffect(() => {
		const fetchData = async () => {
			try {
				// using memoized to prevent unnecessary network requests
				const totalWorkflows = await memoizedGetWorkflowsCount({
					cloudId,
					projectAndIssueTypes: issueTypes.map((it) => ({
						projectId,
						issueTypeId: it.id,
					})),
					subtaskIssueTypeId,
					fetchWorkflowsV2,
				});

				setState({
					data: {
						hasMultipleWorkflows: totalWorkflows > 1,
						hasExceededMaxIssueTypes,
					},
					loading: false,
					error: undefined,
				});
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (e: any) {
				setState({
					data: undefined,
					loading: false,
					error: e,
				});
			}
		};

		if (shouldFetchOnMount && !loading) {
			// Do not call fetch since this will return this error: size must be between 0 and 25
			if (hasExceededMaxIssueTypes) {
				setState({
					data: { hasExceededMaxIssueTypes, hasMultipleWorkflows: false },
					loading: false,
					error: undefined,
				});
			} else {
				fetchData();
			}
		}
	}, [
		cloudId,
		issueTypes,
		shouldFetchOnMount,
		projectId,
		subtaskIssueTypeId,
		loading,
		fetchWorkflowsV2,
		hasExceededMaxIssueTypes,
	]);

	return state;
};
